import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { collection, query, getDocs, orderBy, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { stripeApi } from '../../services/stripeApiService';
import { buildStripeCouponPayload } from '../../utils/stripeCouponPayload';
import { filterVisiblePromos, countInactivePromos } from '../../utils/couponFilters';





export const AdminCoupons: React.FC = () => {
  const { theme } = useTheme();
  const [tab, setTab] = useState(0);

  // Stripe States (Disabled/Unused)
  // const [coupons, setCoupons] = useState<Coupon[]>([]);
  // const [promotionCodes, setPromotionCodes] = useState<PromotionCode[]>([]);

  // Internal States
  const [internalCoupons, setInternalCoupons] = useState<any[]>([]);

  // Stripe coupons + promotion codes
  const [stripeCoupons, setStripeCoupons] = useState<any[]>([]);
  const [stripePromos, setStripePromos] = useState<any[]>([]);
  const [stripeProducts, setStripeProducts] = useState<any[]>([]);
  const [stripeDialogOpen, setStripeDialogOpen] = useState(false);
  const [stripeCreating, setStripeCreating] = useState(false);
  const [stripeFormData, setStripeFormData] = useState({
    code: '',
    name: '',
    percentOff: '30',
    duration: 'once' as 'once' | 'repeating' | 'forever',
    durationInMonths: '',
    maxRedemptions: '',
    appliesToProducts: [] as string[],
  });
  const [deleteStripe, setDeleteStripe] = useState<{ open: boolean, couponId: string | null, code: string | null }>({ open: false, couponId: null, code: null });
  const [showInactivePromos, setShowInactivePromos] = useState(false);

  const [loading, setLoading] = useState(true);
  const [internalDialogOpen, setInternalDialogOpen] = useState(false); // New dialog for internal
  const [deleteData, setDeleteData] = useState<{ open: boolean, id: string | null }>({ open: false, id: null });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // const [formData, setFormData] = useState({ ... }); 
  // const [promoCodeFormData, setPromoCodeFormData] = useState({ ... });



  // Internal Form Data
  const [internalFormData, setInternalFormData] = useState({
    code: '',
    plan: 'premium',
    durationInMonths: '1',
    maxRedemptions: '',
  });

  /* 
  const loadStripeCoupons = async () => { ... }
  */

  const loadInternalCoupons = async () => {
    try {
      const q = query(collection(db, 'internal_coupons'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setInternalCoupons(data);
    } catch (err) {
      console.error('Error loading internal coupons', err);
    }
  };

  const loadStripeData = async () => {
    try {
      const [couponsRes, promosRes, productsRes] = await Promise.all([
        stripeApi.listCoupons({ limit: 100 }),
        stripeApi.listPromotionCodes({ limit: 100 }),
        stripeApi.listProducts({ active: true, limit: 100 }),
      ]);
      if (couponsRes.success) setStripeCoupons((couponsRes.data as any)?.coupons?.data || []);
      if (promosRes.success) setStripePromos((promosRes.data as any)?.promotionCodes?.data || []);
      if (productsRes.success) setStripeProducts((productsRes.data as any)?.products?.data || []);
    } catch {
      // erros de carregamento são exibidos via state
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([loadInternalCoupons(), loadStripeData()]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStripeCoupon = async () => {
    const validation = buildStripeCouponPayload(stripeFormData);
    if (!validation.ok) {
      setError(validation.error);
      return;
    }
    try {
      setStripeCreating(true);
      setError(null);

      const payload: any = { ...validation.payload };
      if (stripeFormData.appliesToProducts.length > 0) {
        payload.appliesToProducts = stripeFormData.appliesToProducts;
      }
      const couponRes = await stripeApi.createCoupon(payload);
      if (!couponRes.success) throw new Error(couponRes.error || 'Erro ao criar cupom Stripe');

      const couponId = (couponRes.data as any)?.coupon?.id;
      if (!couponId) throw new Error('ID do cupom não retornado');

      const promoRes = await stripeApi.createPromotionCode({
        coupon: couponId,
        code: stripeFormData.code.toUpperCase(),
        ...(stripeFormData.maxRedemptions ? { max_redemptions: parseInt(stripeFormData.maxRedemptions) } : {}),
      });
      if (!promoRes.success) throw new Error(promoRes.error || 'Cupom criado, mas erro ao criar código');

      setSuccess(`Cupom ${stripeFormData.code.toUpperCase()} criado!`);
      setStripeDialogOpen(false);
      setStripeFormData({ code: '', name: '', percentOff: '30', duration: 'once', durationInMonths: '', maxRedemptions: '', appliesToProducts: [] });
      await loadStripeData();
    } catch (e: any) {
      setError(e.message || 'Erro ao criar cupom Stripe');
    } finally {
      setStripeCreating(false);
    }
  };

  const confirmDeleteStripe = async () => {
    if (!deleteStripe.couponId) return;
    try {
      if (deleteStripe.code) {
        const promo = await stripeApi.findActivePromotionCodeByName(deleteStripe.code);
        if (promo?.id) {
          const deactivated = await stripeApi.deactivatePromotionCode(promo.id);
          if (!deactivated.success) throw new Error(deactivated.error || 'Erro ao desativar o código do cupom');
        }
      }
      const res = await stripeApi.deleteCoupon(deleteStripe.couponId);
      if (!res.success) throw new Error(res.error || 'Erro ao deletar');
      setSuccess('Cupom Stripe deletado e código desativado');
      await loadStripeData();
    } catch (e: any) {
      setError(e.message || 'Erro ao deletar cupom');
    }
    setDeleteStripe({ open: false, couponId: null, code: null });
  };

  useEffect(() => {
    loadData();
  }, []);

  /* 
  const handleCreateCoupon = async () => { ... };
  const handleDeleteCoupon = async (couponId: string) => { ... };
  const handleCreatePromotionCode = async () => { ... };
  */
  // The `AdminCoupons` is 500 lines. Replacing everything is safer to ensure structure.

  // Re-implementing existing handlers for brevity/correctness in restart
  // ... handleCreateCoupon logic identical to before ...
  // To save token space and avoid complexity in this huge replace, I will actually use a trick: 
  // I will implementation the NEW handlers and then the UI.

  const handleCreateInternalCoupon = async () => {
    try {
      if (!internalFormData.code) return;

      await addDoc(collection(db, 'internal_coupons'), {
        code: internalFormData.code.toUpperCase(),
        plan: internalFormData.plan,
        durationInMonths: parseInt(internalFormData.durationInMonths),
        maxRedemptions: internalFormData.maxRedemptions ? parseInt(internalFormData.maxRedemptions) : null,
        redemptions: 0,
        active: true,
        createdAt: serverTimestamp(),
        createdBy: 'admin' // In real app get user ID
      });

      setSuccess('Cupom interno criado!');
      setInternalDialogOpen(false);
      setInternalFormData({ code: '', plan: 'premium', durationInMonths: '1', maxRedemptions: '' });
      loadInternalCoupons();
    } catch (e) {
      console.error(e);
      setError('Erro ao criar cupom interno');
    }
  };


  const confirmDelete = async () => {
    if (!deleteData.id) return;
    try {
      await deleteDoc(doc(db, 'internal_coupons', deleteData.id));
      setSuccess('Deletado com sucesso');
      loadInternalCoupons();
    } catch (e) { setError('Erro ao deletar'); }
    setDeleteData({ open: false, id: null });
  };

  const handleDeleteInternal = (id: string) => {
    setDeleteData({ open: true, id });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copiado!');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label="Carregando" sx={{ color: theme.colors.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
          Cupons e Promoções
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 0 }}>
            <Tab label="Internos (Acesso)" />
            <Tab label="Stripe (Descontos)" />
          </Tabs>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton onClick={loadData} aria-label="Recarregar cupons" sx={{ color: theme.colors.primary }}>
            <RefreshIcon />
          </IconButton>
          {tab === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setInternalDialogOpen(true)}
              sx={{ backgroundColor: theme.colors.primary, color: 'white' }}
            >
              Novo Cupom Interno
            </Button>
          )}
          {tab === 1 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setStripeDialogOpen(true)}
              sx={{ backgroundColor: theme.colors.primary, color: 'white' }}
            >
              Novo Cupom Stripe
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {tab === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Cupons internos dão acesso gratuito a um plano por tempo determinado. O usuário pode resgatar na tela de assinatura.
          </Alert>
          <TableContainer component={Paper} sx={{ backgroundColor: theme.colors.card }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Plano Liberado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Duração</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Usos / Limite</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {internalCoupons.map(c => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{c.code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          display: 'inline-block',
                          backgroundColor: c.plan === 'pro' ? '#4caf50' : '#ff9800',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.85rem'
                        }}
                      >
                        {c.plan === 'premium' ? 'Basic' : c.plan === 'pro' ? 'Pro' : c.plan}
                      </Typography>
                    </TableCell>
                    <TableCell>{c.durationInMonths} {c.durationInMonths === 1 ? 'mês' : 'meses'}</TableCell>
                    <TableCell>{c.redemptions || 0} / {c.maxRedemptions || '∞'}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => copyToClipboard(c.code)} aria-label={`Copiar código ${c.code}`}><CopyIcon /></IconButton>
                      <IconButton onClick={() => handleDeleteInternal(c.id)} color="error" aria-label={`Excluir cupom ${c.code}`}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {internalCoupons.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Nenhum cupom criado ainda</Typography>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setInternalDialogOpen(true)}
                        sx={{ mt: 2 }}
                      >
                        Criar primeiro cupom
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Cupons Stripe aplicam <b>desconto percentual</b> no checkout. O usuário insere o código diretamente na tela de pagamento do Stripe.
          </Alert>
          {(() => {
            const inactiveCount = countInactivePromos(stripePromos);
            if (inactiveCount === 0) return null;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {showInactivePromos
                    ? `${inactiveCount} cupom(ns) inativo(s) exibido(s)`
                    : `${inactiveCount} cupom(ns) inativo(s) oculto(s)`}
                </Typography>
                <Button size="small" onClick={() => setShowInactivePromos(v => !v)} sx={{ textTransform: 'none' }}>
                  {showInactivePromos ? 'Ocultar inativos' : 'Mostrar inativos'}
                </Button>
              </Box>
            );
          })()}
          <TableContainer component={Paper} sx={{ backgroundColor: theme.colors.card }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Desconto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Duração</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Usos / Limite</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filterVisiblePromos(stripePromos, showInactivePromos).map((promo: any) => {
                  const coupon = promo.coupon || stripeCoupons.find((c: any) => c.id === promo.coupon);
                  const couponData = typeof promo.coupon === 'object' ? promo.coupon : coupon;
                  const duration = couponData?.duration === 'once' ? 'Uma vez'
                    : couponData?.duration === 'forever' ? 'Para sempre'
                    : couponData?.duration === 'repeating' ? `${couponData?.duration_in_months || 0} meses`
                    : '-';
                  const discount = couponData?.percent_off ? `${couponData.percent_off}%`
                    : couponData?.amount_off ? `${(couponData.amount_off / 100).toFixed(2)} ${couponData.currency?.toUpperCase()}`
                    : '-';
                  return (
                    <TableRow key={promo.id}>
                      <TableCell>
                        <Typography sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{promo.code}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 'bold', color: '#4caf50' }}>-{discount}</Typography>
                      </TableCell>
                      <TableCell>{duration}</TableCell>
                      <TableCell>{promo.times_redeemed || 0} / {promo.max_redemptions || '∞'}</TableCell>
                      <TableCell>
                        <Typography sx={{
                          px: 1.5, py: 0.5, borderRadius: 1, display: 'inline-block',
                          backgroundColor: promo.active ? '#4caf50' : '#999',
                          color: 'white', fontSize: '0.8rem', fontWeight: 'bold'
                        }}>
                          {promo.active ? 'Ativo' : 'Inativo'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => copyToClipboard(promo.code)} aria-label={`Copiar código ${promo.code}`}><CopyIcon /></IconButton>
                        <IconButton
                          onClick={() => setDeleteStripe({ open: true, couponId: couponData?.id || promo.coupon, code: promo.code })}
                          color="error"
                          aria-label={`Excluir cupom ${promo.code}`}
                        ><DeleteIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filterVisiblePromos(stripePromos, showInactivePromos).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {stripePromos.length === 0 ? 'Nenhum cupom Stripe criado ainda' : 'Nenhum cupom ativo. Use "Mostrar inativos" para ver os desativados.'}
                      </Typography>
                      {stripePromos.length === 0 && (
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setStripeDialogOpen(true)}
                          sx={{ mt: 2 }}
                        >
                          Criar primeiro cupom
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Internal Dialog */}
      <Dialog aria-labelledby="admincoupons-dlg1-title" open={internalDialogOpen} onClose={() => setInternalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle id="admincoupons-dlg1-title" sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Criar Cupom de Acesso</Typography>
          <Typography variant="body2" color="text.secondary">
            Cupons internos dão acesso gratuito ao plano por tempo determinado
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Código do Cupom *
            </Typography>
            <TextField
              placeholder="Ex: PROMO2024, INFLUENCER, VIP"
              value={internalFormData.code}
              onChange={e => setInternalFormData({ ...internalFormData, code: e.target.value.toUpperCase() })}
              fullWidth
              helperText="O código que o usuário vai digitar para resgatar"
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Qual plano o cupom libera? *
            </Typography>
            <TextField
              select
              SelectProps={{ native: true }}
              value={internalFormData.plan}
              onChange={e => setInternalFormData({ ...internalFormData, plan: e.target.value })}
              fullWidth
            >
              <option value="premium">Basic (Plano intermediário)</option>
              <option value="pro">Pro (Plano completo)</option>
            </TextField>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Por quanto tempo? (em meses) *
            </Typography>
            <TextField
              type="number"
              value={internalFormData.durationInMonths}
              onChange={e => setInternalFormData({ ...internalFormData, durationInMonths: e.target.value })}
              fullWidth
              inputProps={{ min: 1 }}
              helperText="Ex: 1 = 1 mês, 12 = 1 ano"
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Quantas pessoas podem usar? (opcional)
            </Typography>
            <TextField
              type="number"
              placeholder="Deixe vazio para ilimitado"
              value={internalFormData.maxRedemptions}
              onChange={e => setInternalFormData({ ...internalFormData, maxRedemptions: e.target.value })}
              fullWidth
              inputProps={{ min: 1 }}
              helperText="Deixe em branco se não tiver limite de usos"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setInternalDialogOpen(false)} color="inherit">Cancelar</Button>
          <Button
            onClick={handleCreateInternalCoupon}
            variant="contained"
            disabled={!internalFormData.code || !internalFormData.durationInMonths}
          >
            Criar Cupom
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog aria-labelledby="admincoupons-dlg2-title" open={deleteData.open} onClose={() => setDeleteData({ open: false, id: null })}>
        <DialogTitle id="admincoupons-dlg2-title">Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir este cupom permanentemente?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteData({ open: false, id: null })}>Cancelar</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* Stripe Coupon Dialog */}
      <Dialog aria-labelledby="admincoupons-dlg3-title" open={stripeDialogOpen} onClose={() => setStripeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle id="admincoupons-dlg3-title" sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Criar Cupom Stripe (Desconto)</Typography>
          <Typography variant="body2" color="text.secondary">
            Cupons Stripe dão desconto percentual no checkout. O usuário aplica diretamente na tela de pagamento.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Código do Cupom *</Typography>
            <TextField
              placeholder="Ex: HOSTLY30, CONFERENCIA, BLACKFRIDAY"
              value={stripeFormData.code}
              onChange={e => setStripeFormData({ ...stripeFormData, code: e.target.value.toUpperCase() })}
              fullWidth
              helperText="O código que o usuário vai digitar no checkout"
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Nome interno (opcional)</Typography>
            <TextField
              placeholder="Ex: Conferência Abril 2026"
              value={stripeFormData.name}
              onChange={e => setStripeFormData({ ...stripeFormData, name: e.target.value })}
              fullWidth
              helperText="Para identificar no painel do Stripe"
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Percentual de desconto (%) *</Typography>
            <TextField
              type="number"
              value={stripeFormData.percentOff}
              onChange={e => setStripeFormData({ ...stripeFormData, percentOff: e.target.value })}
              fullWidth
              inputProps={{ min: 1, max: 100 }}
              helperText="Ex: 30 = 30% off"
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Duração do desconto *</Typography>
            <TextField
              select
              SelectProps={{ native: true }}
              value={stripeFormData.duration}
              onChange={e => setStripeFormData({ ...stripeFormData, duration: e.target.value as any })}
              fullWidth
              helperText="'Uma vez' aplica só no primeiro pagamento. Para plano anual, isso já é o ano inteiro."
            >
              <option value="once">Uma vez (primeiro pagamento — ideal pra anual)</option>
              <option value="repeating">Repetir por X meses</option>
              <option value="forever">Para sempre</option>
            </TextField>
          </Box>

          {stripeFormData.duration === 'repeating' && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Por quantos meses?</Typography>
              <TextField
                type="number"
                value={stripeFormData.durationInMonths}
                onChange={e => setStripeFormData({ ...stripeFormData, durationInMonths: e.target.value })}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Limite de usos (opcional)</Typography>
            <TextField
              type="number"
              placeholder="Deixe vazio para ilimitado"
              value={stripeFormData.maxRedemptions}
              onChange={e => setStripeFormData({ ...stripeFormData, maxRedemptions: e.target.value })}
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Restringir a produtos (opcional)</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
              Se nenhum for marcado, o cupom vale pra todos os planos. Stripe restringe por produto (não por preço).
            </Typography>
            {stripeProducts.length === 0 ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Nenhum produto Stripe encontrado.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {stripeProducts.map((p: any) => {
                  const selected = stripeFormData.appliesToProducts.includes(p.id);
                  return (
                    <Box
                      component="label"
                      key={p.id}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 1,
                        border: `1px solid ${selected ? theme.colors.primary : theme.colors.border}`,
                        borderRadius: 1, cursor: 'pointer',
                        backgroundColor: selected ? `${theme.colors.primary}10` : 'transparent',
                        '&:focus-within': { outline: `2px solid ${theme.colors.primary}`, outlineOffset: 2 },
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {
                          const next = selected
                            ? stripeFormData.appliesToProducts.filter(id => id !== p.id)
                            : [...stripeFormData.appliesToProducts, p.id];
                          setStripeFormData({ ...stripeFormData, appliesToProducts: next });
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name || p.id}</Typography>
                        {p.description && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{p.description}</Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setStripeDialogOpen(false)} color="inherit" disabled={stripeCreating}>Cancelar</Button>
          <Button
            onClick={handleCreateStripeCoupon}
            variant="contained"
            disabled={!stripeFormData.code || !stripeFormData.percentOff || stripeCreating}
          >
            {stripeCreating ? <CircularProgress aria-label="Carregando" size={20} /> : 'Criar Cupom'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Stripe Coupon Confirmation */}
      <Dialog aria-labelledby="admincoupons-dlg4-title" open={deleteStripe.open} onClose={() => setDeleteStripe({ open: false, couponId: null, code: null })}>
        <DialogTitle id="admincoupons-dlg4-title">Excluir cupom Stripe</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir o cupom <b>{deleteStripe.code}</b>? Ele deixará de funcionar imediatamente.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteStripe({ open: false, couponId: null, code: null })}>Cancelar</Button>
          <Button onClick={confirmDeleteStripe} color="error" variant="contained">Excluir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
